import { create } from 'zustand';

interface BulkEmailBatch {
  batchId: string;
  total: number;
  sent: number;
  failed: number;
  status: 'processing' | 'completed' | 'error' | 'unknown';
  onComplete?: () => void;
}

interface BulkEmailProgressStore {
  activeBatch: BulkEmailBatch | null;

  startBatch: (batchId: string, total: number, onComplete?: () => void) => void;
  updateProgress: (batchId: string, sent: number, failed: number) => void;
  completeBatch: (batchId: string, sent: number, failed: number) => void;
  failBatch: (batchId: string) => void;
  markUnknown: (batchId: string, sent: number, failed: number) => void;
  clearBatch: (batchId?: string) => void;
}

const useBulkEmailProgressStore = create<BulkEmailProgressStore>((set, get) => ({
  activeBatch: null,

  startBatch: (batchId, total, onComplete) => {
    set({
      activeBatch: {
        batchId,
        total,
        sent: 0,
        failed: 0,
        status: 'processing',
        onComplete,
      },
    });
  },

  updateProgress: (batchId, sent, failed) => {
    const { activeBatch } = get();
    if (!activeBatch || activeBatch.batchId !== batchId || activeBatch.status !== 'processing')
      return;

    // Pusher and polling can arrive out of order. Keep the furthest known
    // recipient results and avoid publishing an identical Zustand state (which
    // would otherwise re-run status reconciliation indefinitely).
    const nextSent = Math.max(activeBatch.sent, sent);
    const nextFailed = Math.max(activeBatch.failed, failed);
    if (nextSent === activeBatch.sent && nextFailed === activeBatch.failed) return;

    set({
      activeBatch: { ...activeBatch, sent: nextSent, failed: nextFailed },
    });
  },

  completeBatch: (batchId, sent, failed) => {
    const { activeBatch } = get();
    if (!activeBatch || activeBatch.batchId !== batchId || activeBatch.status !== 'processing')
      return;

    const onCompleteCallback = activeBatch.onComplete;

    set({
      activeBatch: { ...activeBatch, sent, failed, status: 'completed' },
    });

    onCompleteCallback?.();
  },

  markUnknown: (batchId, sent, failed) => {
    const { activeBatch } = get();
    if (!activeBatch || activeBatch.batchId !== batchId || activeBatch.status !== 'processing')
      return;
    set({ activeBatch: { ...activeBatch, sent, failed, status: 'unknown' } });
  },

  failBatch: (batchId) => {
    const { activeBatch } = get();
    if (!activeBatch || activeBatch.batchId !== batchId || activeBatch.status !== 'processing')
      return;
    set({ activeBatch: { ...activeBatch, status: 'error' } });
  },

  clearBatch: (batchId) => {
    if (batchId && get().activeBatch?.batchId !== batchId) return;
    set({ activeBatch: null });
  },
}));

export default useBulkEmailProgressStore;
